import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { MapPin, Shield, Loader2, Phone, Mail } from 'lucide-react';
import { BRAND } from './kit/tokens';
import { formatDate, formatDateTime } from './kit/time';
import BorrowerProgress from './shared/BorrowerProgress';
import usePolling from './usePolling';
import { formatPhone, telHref } from './shared/phone';
import { LINK_PAUSED_COPY } from './errors';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

// The footer says the page updates itself; usePolling pauses while the tab is hidden.
const REFRESH_MS = 60000;

// Raw loan-status names, still used by TransactionStatusPublicPage for the linked loan.
export const STATUS_LABEL = {
  prospect: 'Prospect', pre_qual: 'Pre-Qual', pre_approved: 'Pre-Approved',
  application_received: 'Application Received', processing: 'Processing',
  submitted_to_uw: 'Submitted to Underwriting', conditional_approval: 'Conditional Approval',
  clear_to_close: 'Clear to Close', closing_scheduled: 'Closing Scheduled', funded: 'Funded',
  cancelled: 'Cancelled', denied: 'Denied', withdrawn: 'Withdrawn',
};

// 404 = no such token (typo, or the link was replaced); 410 = the link was turned
// off or the loan has closed. Both end with the same step: ask for a new link.
// 403 = the workspace is suspended (ticket 315): the link is paused, not gone.
const errorFor = (status) => {
  if (status === 403) return LINK_PAUSED_COPY;
  if (status === 404) return 'This loan status link is invalid or has been replaced. Please request a new link from your loan officer.';
  if (status === 410) return 'This loan status link has been turned off, or the loan is closed. Please ask your loan officer if you need a new link.';
  return 'Unable to load loan status. Please try again in a moment.';
};

export default function LoanStatusPublicPage() {
  const { shareToken } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  const load = () => axios.get(`${BACKEND}/api/los/status/${shareToken}`)
    .then((r) => setData(r.data))
    .catch((e) => {
      const status = e.response?.status;
      // A refresh that fails keeps the page the borrower already has; only a
      // link that is gone or paused (403, 404, 410) replaces it.
      if ([403, 404, 410].includes(status)) setErr(errorFor(status));
      else if (!data) setErr(errorFor(status));
    });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [shareToken]);
  usePolling(load, REFRESH_MS, { enabled: !err, immediate: false });

  if (err) return (
    <div className="min-h-screen flex items-center justify-center p-6" style={{ background: '#faf7f2' }}>
      <div className="bg-white rounded-2xl p-8 max-w-md text-center shadow">
        <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4" style={{ background: '#FEF3F2' }}>
          <Shield style={{ color: '#dc2626' }} />
        </div>
        <div className="font-serif text-lg font-bold text-brand-navy">Loan Status Unavailable</div>
        <p className="text-slate-500 mt-2 text-sm">{err}</p>
      </div>
    </div>
  );

  if (!data) return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: '#faf7f2' }}>
      <div className="text-slate-400 flex items-center gap-2"><Loader2 className="animate-spin" size={16} /> Loading…</div>
    </div>
  );

  const addr = data.property_address || {};
  const cityState = [addr.city, addr.state].filter(Boolean).join(', ');

  return (
    <div className="min-h-screen" style={{ background: '#faf7f2' }}>
      {/* Hero */}
      <div className="text-white" style={{ background: `linear-gradient(160deg, ${BRAND.navyDeep} 0%, ${BRAND.navy} 60%, ${BRAND.navyLight} 100%)` }}>
        <div className="max-w-4xl mx-auto px-6 py-10">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-brand-accent-soft">
            <Shield size={12} /> Live Loan Status
          </div>
          <h1 className="font-serif text-3xl md:text-4xl font-bold mt-2">{data.borrower_name}</h1>
          <div className="text-white/70 mt-1 flex items-center gap-2 text-sm">
            <MapPin size={13} /> {addr.street}{cityState && ` · ${cityState}`} {addr.zip}
          </div>
          <div className="mt-4 flex items-center gap-2 flex-wrap">
            <span className="font-mono text-xs font-semibold px-2.5 py-1 rounded" style={{ background: 'rgba(255,255,255,0.1)' }}>{data.loan_number}</span>
            <span className="text-xs font-bold uppercase tracking-widest px-2.5 py-1 rounded bg-brand-accent-soft text-brand-navy">{data.progress?.label}</span>
            <span className="text-xs uppercase tracking-widest text-white/50">{data.loan_program} · {data.loan_purpose}</span>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-8 grid md:grid-cols-3 gap-4">
        {/* Progress bar */}
        <div className="md:col-span-3 bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
          <div className="font-serif font-semibold text-lg mb-4 text-brand-navy">Progress</div>
          <BorrowerProgress progress={data.progress} />
        </div>

        {/* Stats */}
        <StatCard label="Open Conditions" value={data.open_conditions_count} color="#f59e0b" />
        <StatCard label="Cleared Conditions" value={data.cleared_conditions_count} color="#22c55e" />
        <StatCard label="Rate Locked" value={data.rate_locked ? 'Yes' : 'No'} sub={data.lock_expiration ? `expires ${formatDate(data.lock_expiration)}` : ''} color={BRAND.navy} />

        {/* Timeline */}
        <div className="md:col-span-3 bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
          <div className="font-serif font-semibold text-lg mb-4 text-brand-navy">Timeline</div>
          <div className="relative pl-6 border-l-2 border-slate-100 space-y-4">
            {data.milestones.slice().reverse().map((m, i) => (
              <div key={i} className="relative">
                <div className="absolute -left-[29px] top-1 w-4 h-4 rounded-full border-2 border-white shadow" style={{ background: i === 0 ? BRAND.accent : '#94a3b8' }} />
                <div className="text-xs uppercase tracking-widest text-slate-400">{formatDateTime(m.occurred_at)}</div>
                <div className="font-semibold text-sm mt-0.5 text-brand-navy">{m.label || m.key}</div>
              </div>
            ))}
            {data.milestones.length === 0 && <div className="text-sm text-slate-400 italic">No milestones yet.</div>}
          </div>
        </div>

        {data.lo_contact && (
          <div className="md:col-span-3 bg-white rounded-2xl p-6 shadow-sm border border-slate-100" data-testid="lo-contact">
            <div className="font-serif font-semibold text-lg mb-2 text-brand-navy">Your loan officer</div>
            <div className="font-semibold text-sm text-brand-navy">{data.lo_contact.name}</div>
            <div className="text-sm text-slate-600 mt-1 space-y-1">
              {data.lo_contact.phone && (
                <div className="flex items-center gap-2"><Phone size={13} /> <a href={telHref(data.lo_contact.phone)} className="hover:underline">{formatPhone(data.lo_contact.phone)}</a></div>
              )}
              {data.lo_contact.email && (
                <div className="flex items-center gap-2"><Mail size={13} /> <a href={`mailto:${data.lo_contact.email}`} className="hover:underline">{data.lo_contact.email}</a></div>
              )}
              {data.lo_contact.nmls && <div className="text-xs text-slate-500">NMLS #{data.lo_contact.nmls}</div>}
            </div>
          </div>
        )}

        <div className="md:col-span-3 text-center text-xs text-slate-400 pt-4">
          This page updates automatically. Contact your loan officer with any questions.
          <div className="mt-1">Sensitive personal information is redacted from this public view.</div>
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, color, sub }) {
  return (
    <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100">
      <div className="text-xs uppercase tracking-widest text-slate-500">{label}</div>
      <div className="font-serif text-3xl font-bold mt-1" style={{ color }}>{value}</div>
      {sub && <div className="text-xs text-slate-400 mt-1">{sub}</div>}
    </div>
  );
}
